import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AppId } from '../../../../api-client/openapi/models';
import { AclsInfo } from '../../../../api-client/openapi/models/acls-info';
import { AclsSettings } from '../../../../api-client/openapi/models/acls-settings';
import {
  AppsService as AppsApiService,
  LicenseServersService,
} from '../../../../api-client/openapi/services';
import { AppIds } from './appIds';

@Injectable({
  providedIn: 'root',
})
export class AppsService {
  private readonly appsApiService = inject(AppsApiService);
  private readonly licenseServersApiService = inject(LicenseServersService);

  public getAvailableAppIds(): Observable<ReadonlyArray<AppId>> {
    return this.appsApiService.v1AppsGet().pipe(
      map((response) => response.apps.map((app) => app.appId)),
      catchError(() => of([]))
    );
  }

  public hasCbp(): Observable<boolean> {
    return this.hasApp(AppIds.CarBodyPlanner);
  }

  public hasFai(): Observable<boolean> {
    return this.hasApp(AppIds.FeasibilityAi);
  }

  public hasAclsAccess(): Observable<boolean> {
    return this.hasApp(AppIds.Acls);
  }

  public hasAnyApp(): Observable<boolean> {
    return this.getAvailableAppIds().pipe(
      map((availableAppIds) => availableAppIds.length > 0)
    );
  }

  public hasApp(appId: AppId): Observable<boolean> {
    return this.getAvailableAppIds().pipe(
      map((availableAppIds) => availableAppIds.includes(appId))
    );
  }

  /**
   * The logged-in user's own cloud license server, based on the user's customer ID.
   * No role required.
   */
  public getAclsInfo(): Observable<AclsInfo> {
    return this.licenseServersApiService
      .v1AppsAclsInfoGet()
      .pipe(map((response) => response.licenseServer));
  }

   /**
   * The logged-in admin user's own cloud license server, based on the user's customer ID.
   * af-adm or cp-adm only.
   */
  public getAclsSettings(): Observable<AclsSettings> {
    return this.licenseServersApiService
      .v1AppsAclsSettingsGet()
      .pipe(map((response) => response.licenseServer));
  }

  public getAclsApplicationUrl(): Observable<string | undefined> {
    return this.getAclsInfo().pipe(
      map((info) => info.licenseServerApplicationUrl)
    );
  }

  public getAclsStatus() {
    return this.getAclsInfo().pipe(map((info) => info.status));
  }
}
